import { fetchWithSessionCookieAuth } from 'auth-lib-front';
import { Env } from 'troppus-common/shared';

export type SSECallbacks<TEvent> = {
  onEvent: (event: TEvent) => void;
  onError?: (error: Error) => void;
  /** ストリームを最後まで読み取り接続が閉じたときに 1 度だけ呼ばれる。 */
  onStreamComplete?: () => void;
};

export type SSEOptions<TEvent> = {
  parser?: (raw: unknown) => TEvent;
};

/**
 * POST リクエストで SSE ストリームを受け取るユーティリティ。
 * EventSource は GET のみ対応のため、fetch + ReadableStream で実装。
 *
 * troppus の API は projectId をリクエストボディに含めて送る（neuro と異なり `x-project-id` は使わない）。
 */
export const callSseApi = async <TEvent, TRequest = undefined>(
  path: string,
  callbacks: SSECallbacks<TEvent>,
  input?: TRequest,
  signal?: AbortSignal,
  options?: SSEOptions<TEvent>,
): Promise<void> => {
  const url = new URL(path, Env.Var.Api.Domain);

  const response = await fetchWithSessionCookieAuth(url.toString(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: input !== undefined ? JSON.stringify(input) : undefined,
    signal,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `HTTP ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error('Response body is not readable');

  const decoder = new TextDecoder();
  let buffer = '';

  const handleDataLine = (line: string) => {
    if (!line.startsWith('data: ')) return;
    const data = line.slice(6);
    try {
      const raw = JSON.parse(data);
      const event = options?.parser ? options.parser(raw) : (raw as TEvent);
      callbacks.onEvent(event);
    } catch {
      // パース失敗は無視
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        handleDataLine(line);
      }
    }

    if (buffer.length > 0) {
      handleDataLine(buffer);
    }
    callbacks.onStreamComplete?.();
  } finally {
    reader.releaseLock();
  }
};
